<template>
  <div id="pageDashboard">
    <pr-loading v-model="loading" />
    <v-snackbar v-model="showMsg" value="true" right top multi-line :color="msgType" :timeout="msgTime" >
      {{ msgText }}
      <v-btn dark flat @click="showMsg = false">
        {{ $t('OK') }}
      </v-btn>
    </v-snackbar>

    <v-container grid-list-xl fluid>
      <v-layout row wrap>
        <!-- mini statistic start -->
        <v-flex lg3 sm6 xs12>
          <mini-statistic
            icon="fa-shopping-bag"
            :title="!!totalShipments ? totalShipments.toLocaleString() : '0'"
            :sub-title="$t('Total shipments')"
            color="indigo"
            link="shipments"
          >
          </mini-statistic>
        </v-flex>
        <v-flex lg3 sm6 xs12>
          <mini-statistic
            icon="fa-check-circle"
            :title="activeShipments.toLocaleString()"
            :sub-title="$t('Active shipments')"
            color="green lighten-2"
            link="shipments"
          >
          </mini-statistic>
        </v-flex>
        <v-flex lg3 sm6 xs12>
          <mini-statistic
            icon="fa-file-invoice-dollar"
            :title="invoices.toLocaleString()"
            :sub-title="$t('Invoices')"
            color="light-blue"
            link="invoices"
          >
          </mini-statistic>
        </v-flex>
        <v-flex lg3 sm6 xs12>
          <mini-statistic
            icon="fa-money-bill-wave"
            :title="showCOD()"
            :sub-title="$t('COD to receive')"
            color="green"
            link="cod"
          >
          </mini-statistic>
        </v-flex>
        <!-- mini statistic  end -->
      </v-layout>
      <v-card>
        <v-toolbar color="transparent" flat dense card>
          <v-toolbar-title>{{ $t('Statistics') }}</v-toolbar-title>

          <v-btn-toggle v-model="dataFilter" @change="dateFilterChanged">
            <v-tooltip bottom v-for="(type,idx) in dataFilterTypes" :key="idx">
              <v-btn slot="activator" :value="type" flat>{{ type }}</v-btn>
              <span>{{ $t('Tooltip.dataFilter.' + type) }}</span>
            </v-tooltip>
            <v-tooltip bottom>
                <v-btn slot="activator" value="custom" flat>C</v-btn>
                <span>{{ $t('Tooltip.dataFilter.custom') }}</span>
            </v-tooltip>
          </v-btn-toggle>

          <template v-if="dataFilter === 'custom'">
            <v-input-date-picker v-model="dateSince" @input="dateFilterChanged" :label="$t('Date from')" hide-details clearable class="mx-2"></v-input-date-picker>
            <v-input-date-picker v-model="dateUntil" @input="dateFilterChanged" :label="$t('Date to'  )" hide-details clearable class="mx-2"></v-input-date-picker>
          </template>
          <v-spacer></v-spacer>

          <v-tooltip bottom>
            <v-menu slot="activator" offset-y origin="center center" transition="scale-transition">
              <v-btn icon slot="activator">
                <v-icon class="text--secondary">trending_up</v-icon>
              </v-btn>

              <v-card>
                <v-list>
                  <v-list-tile>
                    <v-list-tile-content>
                      <v-list-tile-title>{{ $t('Graph settings') }}</v-list-tile-title>
                      <v-list-tile-sub-title>{{ $t('Select data segregation mode') }}</v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </v-list>

                <v-divider></v-divider>

                <v-card-text>
                  <v-radio-group v-model="dataSegregation" column @change="segregationFilterChanged">
                    <v-radio v-for="val in segregationModes" :key="val"
                      :label="$t('Segregation.' + val)"
                      :value="val"
                    ></v-radio>
                  </v-radio-group>
                </v-card-text>
              </v-card>
            </v-menu>
            <span>{{ $t('Graph settings') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon @click="updateData">
              <v-icon color="light-blue lighten-1" class="fa-spin" v-if="shipmentDataLoading">autorenew</v-icon>
              <v-icon class="text--secondary" v-else>refresh</v-icon>
            </v-btn>
            <span>{{ $t(shipmentDataLoading ? 'Reloading' : 'Reload') }}</span>
          </v-tooltip>
        </v-toolbar>
        <v-card-text>
          <e-chart ref="chart"
            :path-option="[
              ['dataset.source', shipmentData],
              ['color', [color.lightBlue.base, color.green.lighten1, color.lightBlue.lighten1, color.lightBlue.ligthen3]],
              ['legend.show', true],
              ['xAxis.axisLabel.show', true],
              ['yAxis[0].position', 'left'],
              ['yAxis[0].axisLabel.show', true],
              ['yAxis[0].name', ''],
              ['yAxis[1].position', 'right'],
              ['yAxis[1].axisLabel.show', true],
              ['yAxis[1].show', true],
              ['yAxis[1].name', '€'],
              ['yAxis[1].axisLabel.formatter', '{value} €'],
              ['grid.left', '2%'],
              ['grid.bottom', '5%'],
              ['grid.right', '3%'],
              ['series[0].type', 'line'],
              ['series[0].areaStyle', {}],
              ['series[0].smooth', true],
              ['series[0].yAxisIndex', 0],
              ['series[1].smooth', true],
              ['series[1].type', 'bar'],
              ['series[1].areaStyle', {}],
              ['series[1].yAxisIndex', 1],
            ]"
            width="100%"
          >
          </e-chart>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import EChart from '@/components/chart/echart';
import MiniStatistic from '@/components/statistics/MiniStatistic';
import PrLoading from '@/components/Loading';
import VInputDatePicker from "@/components/VInputDatePicker";
import Material from 'vuetify/es5/util/colors';
import { reject } from 'bluebird';
import { stat } from '../resources';
import { mapGetters, mapActions } from "vuex";
import { showDualAmount, showAmount } from '../components/util';

export default {
  components: {
    EChart,
    VInputDatePicker,
    MiniStatistic,
    PrLoading
  },
  data: () => ({
    loading: false,
    msgText: '',
    msgType: 'info',
    msgTime: 3000,
    showMsg: false,

    color: Material,
    totalShipments: 0,
    activeShipments: 0,
    invoices: 0,
    cashOnDelivery: 0,
    cods: {
      price: null, //{ amount: 0, currency: { code: 'EUR', symbol: '€', prefix: true } },
      price_alt: null
    },
    totalUpdated: null,

    dataFilter: '30',
    dataFilterTypes: [ '30', '60', '90' ],
    dataSegregation: 'by_date',
    segregationModes: ['by_date', 'by_week', 'by_week_day', 'by_month'],
    shipmentData: [ {} ],
    shipmentDataLoading: false,

    dateSince: null,
    dateUntil: null
  }),

  computed: {
    ...mapGetters(['dashboardFilter'])
  },

  methods : {
    ...mapActions(['updateDashboardFilter']),

    getDataFromApi() {
      return new Promise((resolve, reject) => {
        let since = this.dateSince;
        let until = this.dateUntil;
        this.shipmentDataLoading = true;

        // check filtering
        let d = Number(this.dataFilter);
        if (!isNaN(d)) {
          until = new Date();
          since = new Date().setDate( until.getDate() - d );
          until = until.valueOf();
        }

        stat.shipments({}, {since, until}).then(response => {
          let b = response.body;
          let a = {};

          // process all kind of segregation modes
          for ( let mode = 0; mode<this.segregationModes.length; mode++) {
            let modeName = this.segregationModes[mode];
            let m = new Map();
            let d = b.domestic;
            if (d)
              d = d[modeName];

            if (d)
              for( let i = 0; i < d.length; i++) {
                let k = d[i].key;
                let kn = modeName === 'by_week_day' ? this.$t('dayOfWeek.' + k) : k;
                m.set(k, { key : kn, domestic : d[i].val, international : 0, shipments : d[i].val });
              }

            d = b.cash_on_delivery;
            if (d)
              d = d[modeName];
            if (d)
              for( let i = 0; i < d.length; i++) {
                let k = d[i].key;
                let o = m.has(k) ? m.get(k) : {} ;
                let v = { cod : Math.round( d[i].val * 100) / 100 };
                if (!o.key) {
                  let kn = modeName === 'by_week_day' ? this.$t('dayOfWeek.' + k) : k;
                  o = Object.assign(o, { key : kn });
                }
                m.set(k, Object.assign( o, v ));
              }


            d = b.international;
            if (d)
              d = d[modeName];
            if (d)
              for( let i = 0; i < d.length; i++) {
                let k = d[i].key;
                let o = m.has(k) ? m.get(k) : {} ;
                let v = { international : d[i].val, shipments : d[i].val + (m.has(k) && m.get(k)['domestic'] ? m.get(k)['domestic'] : 0 ) };
                if (!o.key) {
                  let kn = modeName === 'by_week_day' ? this.$t('dayOfWeek.' + k) : k;
                  o = Object.assign(o, { key : kn });
                }
                m.set(k, Object.assign( o, v ));
              }

            let raw = Array.from(m.keys()).sort();
            let sa = new Array();
            for(let i = 0; i < raw.length; i ++) {
              sa.push(m.get(raw[i]));
            }

            a[modeName] = sa;
          }
          resolve(a);
        }).catch(error => {
          console.log("Error fetching statistics!");
          this.msgType = 'error';
          this.msgText = error.statusText;
          this.msgTime = 3000;
          if (error.status === 403)
          {
            this.msgTime = null;
            this.msgText = this.$t('Access denied');
          }
          this.showMsg = true;
        }).finally(() => {
          this.shipmentDataLoading = false;
        });
      });
    },

    getTotalsFromApi() {
      return new Promise((resolve, reject) => {
        stat.totals().then(response => {
          resolve(response.body);
        }, error => {
          // error
          console.log("Error fetching statistic totals!");
        });
      });
    },

    dateFilterChanged() {
      if (!this.dataFilter)
       return;
      this.storeStatsState();
      this.updateData();
    },

    segregationFilterChanged() {
      this.storeStatsState();
      this.reloadGraphic();
    },

    reloadGraphic() {
      let d = this.shipmentData[this.dataSegregation];

      if (d && d.length > 0) {
        let source = new Array();
        for (let i = 0 ; i < d.length; i ++ )
        {
            let s = {};
            s[this.$t('Day')]       = d[i].key;
            s[this.$t('Shipments')] = d[i].shipments;
            s[this.$t('COD Amount')]= d[i].cod;
            source.push(s);
        }
        if (!!this.$refs && !!this.$refs.chart && !!this.$refs.chart.chartInstance)
          this.$refs.chart.chartInstance.setOption( { dataset : { source }} );
      } else {
        let source = new Array();
        source.push({});
        if (!!this.$refs && !!this.$refs.chart && !!this.$refs.chart.chartInstance)
          this.$refs.chart.chartInstance.setOption( { dataset : { source }} );
      }
    },

    updateTotals() {
      if (this.totalUpdated == null || (new Date().valueOf() - this.totalUpdated) > 5*60*1000 ) {
        this.getTotalsFromApi().then(data => {
          this.invoices = data.invoices;
          this.totalShipments = data.shipments;
          this.activeShipments = data.shipments_active;
          this.cashOnDelivery = !!data.cash_on_delivery ? data.cash_on_delivery : 0;
          this.totalUpdated = new Date().valueOf();
          this.cods = data.cod;
          this.storeStatsState();
        }).error(err => {
          console.log("Error updating totals data", err);
        });
      } else {
        console.log("Totals data from cache");
      }
    },

    updateData() {
      this.getDataFromApi().then(data => {
        this.shipmentData = data;
        this.$nextTick(() => this.reloadGraphic());
        this.shipmentDataLoading = false;
        this.storeStatsState();
      });
    },

    storeStatsState() {
      let t = {
        stamp     : this.totalUpdated,
        shipments : this.totalShipments,
        active    : this.activeShipments,
        invoices  : this.invoices,
        cod       : this.cashOnDelivery,
        cods      : this.cods
      }

      let s = {
        dataFilter : this.dataFilter,
        dataSegregation : this.dataSegregation,
        dateSince : this.dateSince,
        dateUntil : this.dateUntil,
        shipmentData : this.shipmentData,
        totals    : t
      };
      this.updateDashboardFilter(JSON.stringify(s));
    },

    showCOD() {
      if (!!this.cods && !!this.cods.price && !!this.cods.price.amount && !!this.cods.price.currency) {
        let r = showAmount(this.cods.price);
        // TODO: how to show alternative currency nice way?
        // if (this.cods.price_alt && this.cods.price_alt.amount && this.cods.price_alt.currency)
        //   r += ' / ' + showAmount(this.cods.price_alt);
        return r;
      } else {
        return showDualAmount(this.cashOnDelivery)
      }
    }
  },

  mounted () {
    let dashboardFilter = null;
    try {
      dashboardFilter = JSON.parse(this.dashboardFilter);
    } catch(e) {
      console.log("Dashboard filter cannot be parsed");
      dashboardFilter = null;
    }

    if (dashboardFilter) {
      this.dataFilter = dashboardFilter.dataFilter;
      this.dataSegregation = dashboardFilter.dataSegregation;
      this.dateSince = dashboardFilter.dateSince;
      this.dateUntil = dashboardFilter.dateUntil;
      if (dashboardFilter.totals) {
        this.totalUpdated = dashboardFilter.totals.stamp;
        this.totalShipments = dashboardFilter.totals.shipments;
        this.activeShipments = dashboardFilter.totals.active;
        this.invoices = dashboardFilter.totals.invoices;
        this.cashOnDelivery = dashboardFilter.totals.cod;
        this.cods = dashboardFilter.totals.cods;
      }
      if (dashboardFilter.shipmentData) {
        this.shipmentData = dashboardFilter.shipmentData;
        this.reloadGraphic();
      }
    } else {
      let n = new Date();
      this.dateSince = new Date().setMonth(n.getMonth() - 1);
      this.dateUntil = n.valueOf();
    }
    this.updateData();
    this.updateTotals();
  }
}
</script>

<style>

@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
  .v-card .v-card__text a,
  .mini-stats a {
    text-decoration: none !important;
  }
}
</style>